import { m } from 'framer-motion';
import { useState } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

// components
import { varHover } from 'src/components/animate';
import { useSnackbar } from 'src/components/snackbar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import Iconify from 'src/components/iconify';
import { useAuth } from 'src/hooks/useAuth';
import ChangePasswordDialog from './change-password-dialog';

// ----------------------------------------------------------------------

const OPTIONS = [
  {
    label: 'Home',
    linkTo: '/',
  },
  {
    label: 'Profile',
    linkTo: paths.dashboard.user.profile,
  },
  {
    label: 'Settings',
    linkTo: paths.dashboard.user.account,
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const router = useRouter();
  const [openChangePassword, setOpenChangePassword] = useState(false);

  // Use Redux-based useAuth hook
  const { user, logout } = useAuth();

  const { enqueueSnackbar } = useSnackbar();

  const popover = usePopover();

  const handleLogout = async () => {
    try {
      // signOut(); // Call Redux logout action
      logout();
      popover.onClose();
      router.replace('/');
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleClickItem = (path) => {
    popover.onClose();
    if (path === 'change_password') {
      setOpenChangePassword(true);
    } else {
      router.push(path);
    }
  };

  const displayName = user?.displayName || 
                      (user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : null) || 
                      user?.name || 
                      user?.email?.split('@')[0] || 
                      'User';

  const displayDesignation = user?.designation || null;

  return (
    <>
      <Button
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.02)}
        onClick={popover.onOpen}
        color="inherit"
        sx={{
          py: 0.5,
          px: { xs: 0.5, sm: 1.5 },
          borderRadius: 1.5,
          bgcolor: (theme) => popover.open ? alpha(theme.palette.action.selected, 0.08) : 'transparent',
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.action.hover, 0.04),
          },
          textTransform: 'none',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Avatar
            alt={displayName}
            sx={{
              width: 32,
              height: 32,
              border: (theme) => `solid 2px ${theme.palette.background.default}`,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 'bold',
              fontSize: '0.875rem',
            }}
          >
            {displayName.charAt(0).toUpperCase()}
          </Avatar>
          
          <Stack spacing={0.2} sx={{ display: { xs: 'none', md: 'flex' }, textAlign: 'left' }}>
            <Typography variant="subtitle2" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
              {displayName}
            </Typography>
            {displayDesignation && (
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1, textTransform: 'capitalize' }}>
                {displayDesignation}
              </Typography>
            )}
          </Stack>
          
          <Iconify
            icon={popover.open ? 'solar:alt-arrow-up-bold-duotone' : 'solar:alt-arrow-down-bold-duotone'}
            width={16}
            sx={{ color: 'text.secondary', display: { xs: 'none', md: 'inline-block' } }}
          />
        </Stack>
      </Button>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 260, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            alt={displayName}
            sx={{
              width: 48,
              height: 48,
              border: (theme) => `solid 2px ${theme.palette.background.default}`,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 'bold',
            }}
          >
            {displayName.charAt(0).toUpperCase()}
          </Avatar>

          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="subtitle2" noWrap>
              {displayName}
            </Typography>

            {displayDesignation && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textTransform: 'capitalize' }} noWrap>
                {displayDesignation}
              </Typography>
            )}

            <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
              {user?.email}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => (
            <MenuItem key={option.label} onClick={() => handleClickItem(option.linkTo)}>
              {option.label}
            </MenuItem>
          ))}
          
          <MenuItem onClick={() => handleClickItem('change_password')}>
            Change Password
          </MenuItem>
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Logout
        </MenuItem>
      </CustomPopover>

      {/* Change Password Dialog */}
      <ChangePasswordDialog 
        open={openChangePassword} 
        onClose={() => setOpenChangePassword(false)} 
      />
    </>
  );
}
